import { useId, useMemo, useState } from 'react';
import { SlidePanel } from '@/components/composites/SlidePanel';
import { Button } from '@/components/primitives/Button';
import { InfoBanner } from '@/components/primitives/InfoBanner';
import { useCreateRule } from '@/hooks/useRules';
import styles from './AddRulePanel.module.css';

// Task 191 D-C: 'any' is a panel-level pseudo-field. It is NOT in
// `matchFieldSchema` — the submit handler fans it out into four real
// MatchConditions (app | title | url | site) wrapped in `mode: 'any'`.
type ConditionField = 'any' | 'app' | 'title' | 'url' | 'site' | 'document' | 'ip';
type ConditionOperator = 'contains' | 'is' | 'matches';
type ApiMatchType = 'pattern' | 'regex' | 'cidr';

// Task 133 D1.5: inverse of MATCH_TYPE_TO_OPERATOR in useRules.ts. Colocated
// here because AddRulePanel is the only forward-mapping caller today.
function operatorToMatchType(operator: ConditionOperator): ApiMatchType {
  if (operator === 'matches') return 'regex';
  if (operator === 'is') return 'cidr';
  return 'pattern';
}

// Task 191 D-C: the four real fields the 'Any' pseudo-field expands into.
const ANY_FIELDS = ['app', 'title', 'url', 'site'] as const;

export function AddRulePanel({
  projects,
  clients,
  nextPriority,
  onClose,
}: {
  projects: readonly {
    id: string;
    name: string;
    clientId: string | null;
    systemOwned: boolean;
    archivedAt?: string | null;
    slug?: string;
    clientSlug?: string;
  }[];
  clients: readonly {
    id: string;
    name: string;
    archivedAt?: string | null;
    systemOwned: boolean;
  }[];
  nextPriority: number;
  onClose: () => void;
}) {
  const priorityId = useId();
  const clientSelectId = useId();
  const projectId = useId();
  const enabledId = useId();
  const fieldSelectId = useId();
  const opSelectId = useId();
  const valueId = useId();

  const [selectedClientId, setSelectedClientId] = useState('');
  const [priority, setPriority] = useState<number>(nextPriority);
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [conditionField, setConditionField] = useState<ConditionField>('any');
  const [conditionOperator, setConditionOperator] = useState<ConditionOperator>('contains');
  const [conditionValue, setConditionValue] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const createRule = useCreateRule();

  // Visible clients: hide archived. Internal/system-owned clients stay
  // visible — they carry tenant-authored sub-projects (Admin, Training,
  // Development) per task 191 D2.
  const visibleClients = useMemo(() => clients.filter((c) => !c.archivedAt), [clients]);

  // Task 131 D6: hide only the classifier's terminal fallback (Internal/Not
  // Set) and any archived projects. Then filter by selected client when one
  // is chosen.
  const eligibleProjects = useMemo(() => {
    return projects.filter((p) => {
      if (p.archivedAt) return false;
      if (p.systemOwned && p.slug === 'not-set' && p.clientSlug === 'internal') return false;
      if (selectedClientId === '') return false;
      return p.clientId === selectedClientId;
    });
  }, [projects, selectedClientId]);

  const projectSelectDisabled = selectedClientId === '';

  const canSubmit = selectedProjectId !== '' && conditionValue.trim() !== '';

  const handleSubmit = async () => {
    setErrorMessage(null);
    try {
      const trimmedValue = conditionValue.trim();
      const matchType = operatorToMatchType(conditionOperator);

      // Task 191 D-C: 'Any' fans out to four conditions + mode 'any'.
      const match =
        conditionField === 'any'
          ? {
              mode: 'any' as const,
              conditions: ANY_FIELDS.map((f) => ({
                type: matchType,
                field: f,
                value: trimmedValue,
              })),
            }
          : {
              conditions: [
                {
                  type: matchType,
                  field: conditionField,
                  value: trimmedValue,
                },
              ],
            };

      await createRule.mutateAsync({
        priority,
        projectId: selectedProjectId,
        enabled,
        match,
      });
      onClose();
    } catch (err) {
      setErrorMessage(
        err instanceof Error ? err.message : 'Could not save rule — please try again.',
      );
    }
  };

  return (
    <SlidePanel
      open
      onClose={onClose}
      title="Add Rule"
      footer={
        <div className={styles.panelFooter}>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={createRule.isPending || !canSubmit}
          >
            {createRule.isPending ? 'Saving…' : 'Save Rule'}
          </Button>
        </div>
      }
    >
      <div className={styles.panelBody}>
        {errorMessage && <InfoBanner tone="danger">{errorMessage}</InfoBanner>}
        <div className={styles.panelField}>
          <label htmlFor={clientSelectId} className={styles.panelLabel}>
            Client
          </label>
          <select
            id={clientSelectId}
            className={styles.panelSelect}
            value={selectedClientId}
            onChange={(e) => {
              setSelectedClientId(e.target.value);
              // Switching client invalidates any prior project selection.
              setSelectedProjectId('');
            }}
          >
            <option value="" disabled>
              Select a client...
            </option>
            {visibleClients.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>
        <div className={styles.panelField}>
          <label htmlFor={projectId} className={styles.panelLabel}>
            Project
          </label>
          <select
            id={projectId}
            className={styles.panelSelect}
            value={selectedProjectId}
            onChange={(e) => setSelectedProjectId(e.target.value)}
            disabled={projectSelectDisabled}
          >
            <option value="" disabled>
              {projectSelectDisabled ? 'Select a client first...' : 'Select a project...'}
            </option>
            {eligibleProjects.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </div>
        <div className={styles.panelCheckbox}>
          <input
            id={enabledId}
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
          />
          <label htmlFor={enabledId}>Enabled</label>
        </div>
        <div className={styles.panelConditions}>
          <h3 className={styles.panelConditionsTitle}>Match Conditions</h3>
          <div className={styles.panelConditionRow}>
            <label htmlFor={fieldSelectId} className={styles.srOnly}>
              Field
            </label>
            <select
              id={fieldSelectId}
              className={styles.condSelect}
              value={conditionField}
              onChange={(e) => setConditionField(e.target.value as ConditionField)}
            >
              {/* Task 191 D-C: 'Any' goes first; Document/IP after the
                  basic four to discourage casual misuse. */}
              <option value="any">Any</option>
              <option value="app">App</option>
              <option value="title">Title</option>
              <option value="url">URL</option>
              <option value="site">Site</option>
              <option value="document">Document</option>
              <option value="ip">IP</option>
            </select>
            <label htmlFor={opSelectId} className={styles.srOnly}>
              Operator
            </label>
            <select
              id={opSelectId}
              className={styles.condSelect}
              value={conditionOperator}
              onChange={(e) => setConditionOperator(e.target.value as ConditionOperator)}
            >
              <option value="contains">contains</option>
              <option value="is">is</option>
              <option value="matches">matches</option>
            </select>
            <label htmlFor={valueId} className={styles.srOnly}>
              Value
            </label>
            <input
              id={valueId}
              type="text"
              className={styles.condInput}
              placeholder="Value..."
              value={conditionValue}
              onChange={(e) => setConditionValue(e.target.value)}
            />
          </div>
          {/* Task 191 D4: multi-condition authoring deferred to follow-up
              task 192. The 'Any' pseudo-field already covers the most common
              author intent (match across app | title | url | site). */}
          <button
            type="button"
            className={styles.addConditionBtn}
            disabled
            title="Multi-condition rules are coming soon. For now, use 'Any' to match a value across App, Title, URL, and Site."
          >
            + Add condition
          </button>
        </div>
        <div className={styles.panelField}>
          <label htmlFor={priorityId} className={styles.panelLabel}>
            Priority
          </label>
          <input
            id={priorityId}
            type="number"
            className={styles.panelNumberInput}
            value={priority}
            onChange={(e) => {
              const parsed = Number(e.target.value);
              if (Number.isFinite(parsed)) setPriority(parsed);
            }}
          />
        </div>
      </div>
    </SlidePanel>
  );
}
